import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Collapse, Input, List, Popconfirm, Spin, Typography } from 'antd';
import { deleteAgentThread, fetchAgentThread, fetchAgentThreads, streamAgentMessage, } from 'api/agent';
import { useI18n } from 'components/I18nProvider';
import { buildConversationExport, downloadTextFile } from './exportConversation';
import styles from './style.module.scss';
const STAGE_EMOJI = {
    plan: '🧭',
    action: '⚡',
};
const parseJson = (json) => {
    if (!json)
        return null;
    try {
        return JSON.parse(json);
    }
    catch {
        return null;
    }
};
const getStageMeta = (msg) => parseJson(msg.metadataJson);
const finalizeStreamingAssistant = (prev, streamId) => {
    if (!streamId)
        return prev;
    return prev.flatMap(m => {
        if (m.id !== streamId)
            return [m];
        const text = m.streamingText ?? m.content;
        if (!text)
            return [];
        return [{ ...m, streaming: false, content: text, streamingText: undefined }];
    });
};
const dropStreamingAssistant = (prev, streamId) => streamId ? prev.filter(m => m.id !== streamId) : prev;
const isEmptyPlanStage = (msg) => {
    const meta = getStageMeta(msg);
    const text = (msg.streamingText ?? msg.content).trim();
    return msg.kind === 'Thinking' && meta?.stageType === 'plan' && text.length === 0;
};
const isToolMessage = (msg) => msg.kind === 'ToolCall' || msg.kind === 'ToolResult';
const groupMessages = (messages) => {
    const blocks = [];
    const attachToolToLastStage = (toolMsg) => {
        for (let j = blocks.length - 1; j >= 0; j -= 1) {
            const block = blocks[j];
            if (block.type !== 'stage')
                continue;
            const meta = getStageMeta(block.message);
            const preferAction = meta?.stageType === 'action';
            if (preferAction || block.nestedTools.length === 0) {
                blocks[j] = { ...block, nestedTools: [...block.nestedTools, toolMsg] };
                return true;
            }
        }
        for (let j = blocks.length - 1; j >= 0; j -= 1) {
            const block = blocks[j];
            if (block.type === 'stage') {
                blocks[j] = { ...block, nestedTools: [...block.nestedTools, toolMsg] };
                return true;
            }
        }
        return false;
    };
    let i = 0;
    while (i < messages.length) {
        const msg = messages[i];
        if (msg.kind === 'Thinking') {
            if (isEmptyPlanStage(msg)) {
                i += 1;
                continue;
            }
            const nestedTools = [];
            i += 1;
            while (i < messages.length && isToolMessage(messages[i])) {
                nestedTools.push(messages[i]);
                i += 1;
            }
            blocks.push({ type: 'stage', message: msg, nestedTools });
            continue;
        }
        if (isToolMessage(msg)) {
            if (!attachToolToLastStage(msg)) {
                blocks.push({ type: 'message', message: msg });
            }
            i += 1;
            continue;
        }
        blocks.push({ type: 'message', message: msg });
        i += 1;
    }
    return blocks;
};
const MarkdownBody = ({ text, className }) => (_jsx("div", { className: className, children: _jsx(ReactMarkdown, { remarkPlugins: [remarkGfm], children: text }) }));
const AgentSubpage = ({ eventId }) => {
    const { intl } = useI18n();
    const [threads, setThreads] = useState([]);
    const [activeThreadId, setActiveThreadId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const abortRef = useRef(null);
    const streamingAssistantIdRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const stickToBottomRef = useRef(true);
    const loadThreads = useCallback(async () => {
        const list = await fetchAgentThreads(eventId);
        setThreads(list);
    }, [eventId]);
    useEffect(() => {
        loadThreads().catch(console.error);
    }, [loadThreads]);
    const loadThread = useCallback(async (threadId) => {
        setLoading(true);
        try {
            const detail = await fetchAgentThread(eventId, threadId);
            setMessages(detail.messages);
            setActiveThreadId(threadId);
        }
        finally {
            setLoading(false);
        }
    }, [eventId]);
    const handleNewThread = () => {
        abortRef.current?.abort();
        setActiveThreadId(null);
        setMessages([]);
        setInput('');
    };
    const handleDeleteThread = async (threadId) => {
        await deleteAgentThread(eventId, threadId);
        if (activeThreadId === threadId)
            handleNewThread();
        await loadThreads();
    };
    const scrollChatToBottom = useCallback((behavior = 'smooth') => {
        const el = messagesContainerRef.current;
        if (!el)
            return;
        el.scrollTo({ top: el.scrollHeight, behavior });
    }, []);
    useEffect(() => {
        const el = messagesContainerRef.current;
        if (!el)
            return undefined;
        const onScroll = () => {
            const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
            stickToBottomRef.current = distanceFromBottom < 80;
        };
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => el.removeEventListener('scroll', onScroll);
    }, []);
    useEffect(() => {
        if (stickToBottomRef.current) {
            scrollChatToBottom(sending ? 'auto' : 'smooth');
        }
    }, [messages, sending, scrollChatToBottom]);
    const handleExport = () => {
        if (messages.length === 0)
            return;
        const threadTitle = activeThreadId
            ? threads.find(t => t.id === activeThreadId)?.title ?? null
            : null;
        const content = buildConversationExport(eventId, threadTitle, activeThreadId, messages);
        const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        const slug = activeThreadId?.slice(0, 8) ?? 'draft';
        downloadTextFile(`agent-${slug}-${stamp}.txt`, content);
    };
    const handleSend = async () => {
        const content = input.trim();
        if (!content || sending)
            return;
        setSending(true);
        setInput('');
        const threadKey = activeThreadId ?? 'new';
        const userMsg = {
            id: `local-${Date.now()}`,
            kind: 'User',
            content,
            createdAtUtc: new Date().toISOString(),
            orderIndex: messages.length,
        };
        setMessages(prev => [...prev, userMsg]);
        streamingAssistantIdRef.current = null;
        stickToBottomRef.current = true;
        abortRef.current?.abort();
        abortRef.current = new AbortController();
        try {
            for await (const evt of streamAgentMessage(eventId, threadKey, content, abortRef.current.signal)) {
                if (evt.event === 'thread') {
                    const data = evt.data;
                    setActiveThreadId(data.threadId);
                    await loadThreads();
                }
                else if (evt.event === 'token') {
                    const data = evt.data;
                    setMessages(prev => {
                        let streamId = streamingAssistantIdRef.current;
                        if (!streamId) {
                            streamId = `streaming-${Date.now()}`;
                            streamingAssistantIdRef.current = streamId;
                            return [...prev, {
                                    id: streamId,
                                    kind: 'Assistant',
                                    content: '',
                                    createdAtUtc: new Date().toISOString(),
                                    orderIndex: prev.length,
                                    streaming: true,
                                    streamingText: data.text,
                                }];
                        }
                        return prev.map(m => m.id === streamId
                            ? { ...m, streamingText: (m.streamingText ?? '') + data.text }
                            : m);
                    });
                }
                else if (evt.event === 'stage') {
                    const data = evt.data;
                    setMessages(prev => {
                        const streamId = streamingAssistantIdRef.current;
                        streamingAssistantIdRef.current = null;
                        const withoutPreamble = dropStreamingAssistant(prev, streamId);
                        return [...withoutPreamble, {
                                id: data.messageId,
                                kind: 'Thinking',
                                content: '',
                                metadataJson: JSON.stringify({ stageType: data.stageType, title: data.title }),
                                createdAtUtc: new Date().toISOString(),
                                orderIndex: withoutPreamble.length,
                                streaming: true,
                                streamingText: '',
                            }];
                    });
                }
                else if (evt.event === 'stage_token') {
                    const data = evt.data;
                    setMessages(prev => prev.map(m => m.id === data.messageId
                        ? {
                            ...m,
                            streamingText: (m.streamingText ?? m.content) + data.text,
                            content: (m.streamingText ?? m.content) + data.text,
                        }
                        : m));
                }
                else if (evt.event === 'tool_call') {
                    const data = evt.data;
                    setMessages(prev => {
                        const streamId = streamingAssistantIdRef.current;
                        streamingAssistantIdRef.current = null;
                        const base = dropStreamingAssistant(prev, streamId).map(m => m.streaming ? { ...m, streaming: false, streamingText: undefined } : m);
                        return [...base, {
                                id: data.messageId,
                                kind: 'ToolCall',
                                content: data.name,
                                metadataJson: JSON.stringify({ arguments: data.arguments }),
                                createdAtUtc: new Date().toISOString(),
                                orderIndex: base.length,
                            }];
                    });
                }
                else if (evt.event === 'tool_result') {
                    const data = evt.data;
                    setMessages(prev => [...prev, {
                            id: data.messageId,
                            kind: 'ToolResult',
                            content: data.result,
                            metadataJson: JSON.stringify({ name: data.name }),
                            createdAtUtc: new Date().toISOString(),
                            orderIndex: prev.length,
                        }]);
                }
                else if (evt.event === 'done') {
                    setMessages(prev => {
                        const streamId = streamingAssistantIdRef.current;
                        streamingAssistantIdRef.current = null;
                        return finalizeStreamingAssistant(prev, streamId).map(m => m.streaming ? { ...m, streaming: false, streamingText: undefined } : m);
                    });
                }
                else if (evt.event === 'error') {
                    const data = evt.data;
                    const summary = data.summary ?? data.message ?? intl.formatMessage({ id: 'agentError' });
                    const detail = data.detail ?? data.message ?? summary;
                    setMessages(prev => {
                        const streamId = streamingAssistantIdRef.current;
                        streamingAssistantIdRef.current = null;
                        const finalized = dropStreamingAssistant(prev, streamId);
                        return [...finalized, {
                                id: data.messageId ?? `error-${Date.now()}`,
                                kind: 'Error',
                                content: summary,
                                metadataJson: JSON.stringify({ detail }),
                                createdAtUtc: new Date().toISOString(),
                                orderIndex: finalized.length,
                            }];
                    });
                }
            }
        }
        catch (err) {
            if (err.name !== 'AbortError') {
                const streamId = streamingAssistantIdRef.current;
                streamingAssistantIdRef.current = null;
                const detail = err.message;
                setMessages(prev => {
                    const finalized = dropStreamingAssistant(prev, streamId);
                    return [...finalized, {
                            id: `error-${Date.now()}`,
                            kind: 'Error',
                            content: intl.formatMessage({ id: 'agentError' }),
                            metadataJson: JSON.stringify({ detail }),
                            createdAtUtc: new Date().toISOString(),
                            orderIndex: finalized.length,
                        }];
                });
                console.error(err);
            }
        }
        finally {
            setSending(false);
        }
    };
    const renderToolMessage = (msg) => {
        const label = msg.kind === 'ToolCall'
            ? intl.formatMessage({ id: 'agentToolCall' }, { name: msg.content })
            : intl.formatMessage({ id: 'agentToolResult' }, { name: parseJson(msg.metadataJson)?.name ?? '' });
        const body = msg.kind === 'ToolCall' ? msg.metadataJson ?? '' : msg.content;
        return (_jsx(Collapse, { className: styles.nestedToolCollapse, "test-id": "admin-agent-tool-collapse", size: "small", items: [{
                    key: '1',
                    label: _jsxs("span", { className: styles.toolLabel, children: ["\uD83D\uDD27 ", label] }),
                    children: _jsx("pre", { className: styles.toolPre, children: body }),
                }] }, msg.id));
    };
    const renderStageBlock = (block) => {
        const meta = getStageMeta(block.message);
        const stageType = meta?.stageType ?? 'plan';
        const emoji = STAGE_EMOJI[stageType] ?? STAGE_EMOJI.plan;
        const title = meta?.title?.trim()
            || intl.formatMessage({ id: 'agentStageDefault' }, { stage: stageType });
        const text = block.message.streamingText ?? block.message.content;
        const hasContent = text.trim().length > 0;
        const hasTools = block.nestedTools.length > 0;
        const stageClass = stageType === 'action' ? styles.stageAction : styles.stagePlan;
        return (_jsx(Collapse, { className: `${styles.stageCollapse} ${stageClass}`, "test-id": "admin-agent-stage-collapse", items: [{
                    key: '1',
                    label: (_jsxs("span", { className: styles.stageLabel, children: [_jsx("span", { className: styles.stageEmoji, "aria-hidden": true, children: emoji }), _jsx("span", { className: styles.stageTitle, children: title }), block.message.streaming ? _jsx(Spin, { size: "small", className: styles.stageSpinner }) : null] })),
                    children: (_jsxs("div", { className: styles.stageBody, children: [hasContent ? (_jsx(MarkdownBody, { text: text, className: styles.stageMarkdown })) : null, hasTools ? (_jsx("div", { className: hasContent ? styles.stageTools : undefined, children: block.nestedTools.map(renderToolMessage) })) : null, !hasContent && !hasTools ? (_jsx(Typography.Text, { type: "secondary", className: styles.stageEmpty, children: intl.formatMessage({ id: 'agentStageNoDetail' }) })) : null] })),
                }] }, block.message.id));
    };
    const renderMessage = (msg) => {
        if (msg.kind === 'User') {
            return (_jsx("div", { className: styles.userMessageRow, children: _jsx("div", { className: styles.userMessage, children: _jsx(Typography.Text, { children: msg.content }) }) }, msg.id));
        }
        if (msg.kind === 'Error') {
            const detail = parseJson(msg.metadataJson)?.detail ?? msg.content;
            return (_jsx(Collapse, { className: styles.errorCollapse, "test-id": "admin-agent-error-collapse", items: [{
                        key: '1',
                        label: _jsxs("span", { children: ["\u26A0\uFE0F ", msg.content || intl.formatMessage({ id: 'agentError' })] }),
                        children: _jsx("pre", { className: styles.toolPre, children: detail }),
                    }] }, msg.id));
        }
        if (isToolMessage(msg)) {
            return renderToolMessage(msg);
        }
        const text = msg.streamingText ?? msg.content;
        return (_jsxs("div", { className: styles.assistantMessage, children: [_jsx(MarkdownBody, { text: text, className: styles.assistantMarkdown }), msg.streaming && _jsx(Spin, { size: "small" })] }, msg.id));
    };
    const renderBlock = (block) => {
        if (block.type === 'stage')
            return renderStageBlock(block);
        return renderMessage(block.message);
    };
    return (_jsxs("div", { className: styles.agentLayout, children: [_jsxs("div", { className: styles.chatPanel, children: [_jsx("div", { ref: messagesContainerRef, className: styles.messages, children: loading ? _jsx(Spin, {}) : groupMessages(messages).map(block => (_jsx("div", { children: renderBlock(block) }, block.type === 'stage' ? block.message.id : block.message.id))) }), _jsxs("div", { className: styles.inputRow, children: [_jsx(Input.TextArea, { "test-id": "admin-agent-input", value: input, onChange: e => setInput(e.target.value), onPressEnter: e => { if (!e.shiftKey) {
                                    e.preventDefault();
                                    handleSend();
                                } }, placeholder: intl.formatMessage({ id: 'agentInputPlaceholder' }), autoSize: { minRows: 2, maxRows: 6 }, disabled: sending }), _jsx(Button, { "test-id": "admin-agent-send-button", type: "primary", onClick: handleSend, loading: sending, disabled: !input.trim(), children: intl.formatMessage({ id: 'agentSend' }) })] })] }), _jsxs("div", { className: styles.threadSidebar, children: [_jsxs("div", { className: styles.threadToolbar, children: [_jsx(Button, { "test-id": "admin-agent-new-thread-button", icon: _jsx(PlusOutlined, {}), onClick: handleNewThread, className: styles.newThreadBtn, children: intl.formatMessage({ id: 'agentNewThread' }) }), _jsx(Button, { "test-id": "admin-agent-export-button", onClick: handleExport, disabled: messages.length === 0, className: styles.exportBtn, children: intl.formatMessage({ id: 'agentExport' }) })] }), _jsx(List, { dataSource: threads, locale: { emptyText: intl.formatMessage({ id: 'agentNoConversations' }) }, renderItem: item => (_jsx(List.Item, { className: item.id === activeThreadId ? styles.threadActive : styles.threadItem, onClick: () => loadThread(item.id), "test-id": "admin-agent-thread-item", actions: [
                                _jsx(Popconfirm, { title: intl.formatMessage({ id: 'agentDeleteThreadConfirm' }), onConfirm: e => { e?.stopPropagation(); handleDeleteThread(item.id); }, okButtonProps: { 'test-id': 'admin-agent-delete-thread-confirm' }, cancelButtonProps: { 'test-id': 'admin-agent-delete-thread-cancel' }, children: _jsx(Button, { "test-id": "admin-agent-delete-thread-button", type: "text", size: "small", icon: _jsx(DeleteOutlined, {}), onClick: e => e.stopPropagation(), danger: true }) }, "del"),
                            ], children: _jsx(List.Item.Meta, { title: item.title, description: new Date(item.updatedAtUtc).toLocaleString() }) })) })] })] }));
};
export default AgentSubpage;
